import { acceptHMRUpdate, defineStore } from "pinia";
import { toRefs } from "vue";
import type {
  Supervision,
  SupervisionCreatePayload,
  SupervisionUpdatePayload,
  SupervisionFilters,
} from "~/types/supervision";

interface SupervisionState {
  mySupervisions: Supervision[];
  managerSupervisions: Supervision[];
}

export const useSupervisionStore = defineStore("supervision", () => {
  const auth = useAuthStore();
  const toast = useToast();

  const state = ref<SupervisionState>({
    mySupervisions: [],
    managerSupervisions: [],
  });

  // ==================== User Supervision Actions ====================

  const fetchMySupervisions = async () => {
    try {
      const resp = await auth.api<Supervision[]>("get", "/supervision-sessions");
      state.value.mySupervisions = resp;
      return resp;
    } catch (error: any) {
      console.error("Error fetching supervisions:", error);
      const description = error.data?.message || error.message || "Could not fetch supervisions.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const getMySupervision = async (id: string): Promise<Supervision> => {
    try {
      const resp = await auth.api<Supervision>("get", `/supervision-sessions/${id}`);
      return resp;
    } catch (error: any) {
      console.error("Error fetching supervision:", error);
      const description = error.data?.message || error.message || "Could not fetch supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const acknowledgeSupervision = async (id: string) => {
    const index = state.value.mySupervisions.findIndex((s) => s.id === id);
    const snapshot = index !== -1 ? { ...state.value.mySupervisions[index] } : null;

    if (index !== -1) {
      state.value.mySupervisions[index] = {
        ...state.value.mySupervisions[index],
        status: "complete",
        staff_acknowledged_at: new Date().toISOString(),
      };
    }

    try {
      const resp = await auth.api<Supervision>("post", `/supervision-sessions/${id}/acknowledge`);
      if (index !== -1) {
        state.value.mySupervisions[index] = resp;
      }
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Supervision acknowledged",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      if (snapshot && index !== -1) {
        state.value.mySupervisions[index] = snapshot;
      }
      console.error("Error acknowledging supervision:", error);
      const description =
        error.data?.message || error.message || "Could not acknowledge supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const rejectSupervision = async (id: string, rejectionReason: string) => {
    const index = state.value.mySupervisions.findIndex((s) => s.id === id);
    const snapshot = index !== -1 ? { ...state.value.mySupervisions[index] } : null;

    if (index !== -1) {
      state.value.mySupervisions[index] = {
        ...state.value.mySupervisions[index],
        status: "rejected",
        rejection_reason: rejectionReason,
      };
    }

    try {
      const resp = await auth.api<Supervision>("post", `/supervision-sessions/${id}/reject`, {
        rejection_reason: rejectionReason,
      });
      if (index !== -1) {
        state.value.mySupervisions[index] = resp;
      }
      toast.add({
        icon: "i-heroicons-x-circle",
        title: "Supervision Rejected",
        description: "The supervision has been rejected",
        color: "warning",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      if (snapshot && index !== -1) {
        state.value.mySupervisions[index] = snapshot;
      }
      console.error("Error rejecting supervision:", error);
      const description = error.data?.message || error.message || "Could not reject supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  // ==================== Manager Supervision Actions ====================

  const fetchManagerSupervisions = async (filters?: SupervisionFilters) => {
    try {
      const resp = await auth.api<{ data: Supervision[]; meta: any }>(
        "get",
        "/supervision-sessions/manager",
        {
          queryString: filters,
        },
      );
      state.value.managerSupervisions = resp.data;
      return resp;
    } catch (error: any) {
      console.error("Error fetching manager supervisions:", error);
      const description = error.data?.message || error.message || "Could not fetch supervisions.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const createSupervision = async (data: SupervisionCreatePayload) => {
    try {
      const resp = await auth.api<Supervision>("post", "/supervision-sessions", data);
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Supervision created successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error creating supervision:", error);
      const description = error.data?.message || error.message || "Could not create supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const updateSupervision = async (id: string, data: SupervisionUpdatePayload) => {
    try {
      const resp = await auth.api<Supervision>("put", `/supervision-sessions/${id}`, data);
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Supervision updated successfully",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      console.error("Error updating supervision:", error);
      const description = error.data?.message || error.message || "Could not update supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const deleteSupervision = async (id: string) => {
    try {
      await auth.api("delete", `/supervision-sessions/${id}`);
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Supervision deleted successfully",
        color: "success",
        duration: 2500,
      });
      return true;
    } catch (error: any) {
      console.error("Error deleting supervision:", error);
      const description = error.data?.message || error.message || "Could not delete supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  const signSupervision = async (id: string) => {
    const index = state.value.managerSupervisions.findIndex((s) => s.id === id);
    const snapshot = index !== -1 ? { ...state.value.managerSupervisions[index] } : null;

    if (index !== -1) {
      state.value.managerSupervisions[index] = {
        ...state.value.managerSupervisions[index],
        status: "awaiting_acknowledgement",
        manager_signed_at: new Date().toISOString(),
      };
    }

    try {
      const resp = await auth.api<Supervision>("post", `/supervision-sessions/${id}/sign`);
      if (index !== -1) {
        state.value.managerSupervisions[index] = resp;
      }
      toast.add({
        icon: "i-heroicons-document-check",
        title: "Success",
        description: "Supervision signed",
        color: "success",
        duration: 2500,
      });
      return resp;
    } catch (error: any) {
      if (snapshot && index !== -1) {
        state.value.managerSupervisions[index] = snapshot;
      }
      console.error("Error signing supervision:", error);
      const description = error.data?.message || error.message || "Could not sign supervision.";
      toast.add({ title: "Error", description, color: "error" });
      throw new Error(description);
    }
  };

  // ==================== Local Mutation Methods (for SSE handlers) ====================

  const updateMySupervisionLocal = (updatedSupervision: any) => {
    const index = state.value.mySupervisions.findIndex((s) => s.id === updatedSupervision.id);
    if (index !== -1) {
      state.value.mySupervisions[index] = {
        ...state.value.mySupervisions[index],
        ...updatedSupervision,
      };
    }
  };

  const addMySupervisionLocal = (supervision: any) => {
    const exists = state.value.mySupervisions.some((s) => s.id === supervision.id);
    if (!exists) {
      state.value.mySupervisions.unshift(supervision);
    }
  };

  const addOrUpdateManagerSupervisionLocal = (supervision: any) => {
    const index = state.value.managerSupervisions.findIndex((s) => s.id === supervision.id);
    if (index !== -1) {
      state.value.managerSupervisions[index] = {
        ...state.value.managerSupervisions[index],
        ...supervision,
      };
    } else {
      state.value.managerSupervisions.unshift(supervision);
    }
  };

  const removeManagerSupervisionLocal = (id: string) => {
    state.value.managerSupervisions = state.value.managerSupervisions.filter((s) => s.id !== id);
  };

  return {
    ...toRefs(state.value),

    // User actions
    fetchMySupervisions,
    getMySupervision,
    acknowledgeSupervision,
    rejectSupervision,

    // Manager actions
    fetchManagerSupervisions,
    createSupervision,
    updateSupervision,
    deleteSupervision,
    signSupervision,

    // Local mutation methods (for SSE handlers)
    updateMySupervisionLocal,
    addMySupervisionLocal,
    addOrUpdateManagerSupervisionLocal,
    removeManagerSupervisionLocal,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSupervisionStore, import.meta.hot));
}
